import { useMemo } from 'react';

/**
 * A real soundwave, drawn from a clip's measured peak envelope or derived signature.
 *
 * Classic linear form: bars mirrored around a centre line, left to right in
 * time, drawn with SVG path primitives.
 */

function useBars(peaks, bars) {
  return useMemo(() => {
    if (Array.isArray(peaks) && peaks.length > 0) {
      if (peaks.length <= bars) return peaks;
      const step = peaks.length / bars;
      return Array.from({ length: bars }, (_, i) => {
        const from = Math.floor(i * step);
        const to = Math.max(Math.floor((i + 1) * step), from + 1);
        let max = 0;
        for (let j = from; j < to && j < peaks.length; j++) {
          if (peaks[j] > max) max = peaks[j];
        }
        return max;
      });
    }

    return null;
  }, [peaks, bars]);
}

export function Waveform({
  peaks = null,
  seed = '',
  bars = 64,
  height = 48,
  progress = null,      // 0..1 played fraction, or null for no playhead
  color = '#3ACFCF',
  dimColor = '#2b3b47',
  rounded = true,
  showPlayhead = true,
  style = {},
  ariaLabel,
}) {
  const data = useBars(peaks, bars);

  // Honest empty state fallback if data generation failed
  if (!data) {
    return (
      <div
        data-testid="waveform"
        data-empty="true"
        aria-label={ariaLabel || 'No waveform available'}
        style={{
          height,
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          ...style,
        }}
      >
        <div style={{ height: 2, width: '100%', background: dimColor, borderRadius: 2 }} />
      </div>
    );
  }

  const n = data.length;
  const gap = 1;
  const slot = 100 / n;
  const barW = Math.max(0.6, slot - gap * (100 / 640));

  const playedUpTo = progress === null ? 0 : Math.max(0, Math.min(n, Math.round(progress * n)));

  const playedSegments = [];
  const unplayedSegments = [];

  for (let i = 0; i < n; i++) {
    const p = data[i];
    const h = Math.max(1.2, p * 38);
    const y = 20 - h / 2;
    const x = i * slot + (slot - barW) / 2;

    const seg = `M${x.toFixed(3)} ${y.toFixed(3)} h${barW.toFixed(3)} v${h.toFixed(3)} h${(-barW).toFixed(3)} Z`;

    if (i < playedUpTo) {
      playedSegments.push(seg);
    } else {
      unplayedSegments.push(seg);
    }
  }

  const playedPathD = playedSegments.join(' ');
  const unplayedPathD = unplayedSegments.join(' ');

  return (
    <div
      data-testid="waveform"
      data-bars={n}
      aria-label={ariaLabel || 'Sound waveform'}
      style={{ position: 'relative', width: '100%', height, ...style }}
    >
      <svg
        viewBox="0 0 100 40"
        preserveAspectRatio="none"
        width="100%"
        height={height}
        style={{ display: 'block', overflow: 'visible' }}
      >
        <path
          data-testid="waveform-unplayed-path"
          d={unplayedPathD}
          fill={dimColor}
          fillOpacity={0.9}
        />
        <path
          data-testid="waveform-played-path"
          d={playedPathD}
          fill={color}
          fillOpacity={progress === null ? 0.85 : 1}
        />
        {showPlayhead && progress !== null && (
          <rect
            data-testid="waveform-playhead"
            x={Math.max(0, Math.min(100 - 1.6, progress * 100 - 0.8))}
            y={0}
            width={1.6}
            height={40}
            fill={color}
            rx={0.8}
          />
        )}
      </svg>
    </div>
  );
}

export default Waveform;
